<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useCart } from "@/app/Checkout/composables/useCart"
import CartItem from "@/app/Checkout/components/mobile/CartItem/index.vue"
import EditQuantitySheet from "@/app/Checkout/components/mobile/EditQuantitySheet/index.vue"
import NBConfirmModal from "@/app/Checkout/components/common/NBConfirmModal/index.vue"

const props = defineProps({
  /** When true (e.g. checkout context), hide Clear All, QTY, and disable swipe. */
  readonly: { type: Boolean, default: false },
  isClickActionEnabled: { type: Boolean, default: false },
})

const { locale: i18nLocale, t } = useI18n()
const store = useStore()
const router = useRouter()
const { items, deleteCartItem, updateCartItemQuantity, removeAllItems } = useCart()

const pending = computed(() => Boolean(store.state.cart?.pending))
const cartItems = computed(() => items.value ?? [])

const emptyBasketMessage = computed(() => {
  const msg = t('app.cart.empty_basket')
  return (msg && msg !== 'app.cart.empty_basket') ? msg : 'YOUR BASKET IS EMPTY!'
})

/** Locale for URL path - use first path segment (e.g. en-at, de-at) */
function getLocaleForUrl() {
  if (typeof window === 'undefined') return 'en-at'
  const match = window.location.pathname.match(/^\/([a-z]{2}[_-][a-z]{2})\//i)
  if (match) return match[1].toLowerCase().replace('_', '-')
  const fallback = i18nLocale.value || window.config?.locale || 'en-at'
  return String(fallback).toLowerCase().replace(/_/g, '-')
}

const showEditQuantity = ref(false)
const currentEditItem = ref(null)
const showClearConfirm = ref(false)

async function openEditQuantitySheet(item) {
  currentEditItem.value = item
  showEditQuantity.value = true
  await nextTick()
}

function onQuantityConfirm({ item, amount }) {
  const current = item?.quantity ?? item?.amount
  if (item && amount !== current) {
    updateCartItemQuantity({ id: item.id, option: { amount } })
  }
  currentEditItem.value = null
}

function handleClearAll() {
  showClearConfirm.value = true
}

async function handleEditCart() {
  await router.replace({ name: 'Cart' })
}

async function confirmClearAll() {
  await removeAllItems()
  showClearConfirm.value = false
}

function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getProductUrl(item) {
  const product = item?.product
  if (!product) return null
  let url = product.url || product.get_url || product.url_path || item.product_url || item.url
  if (!url) {
    const locale = getLocaleForUrl()
    const id = product.article_number || product.id
    const slug = product.slug || slugify(product.name || product.title || '')
    if (slug && id) {
      url = `/${locale}/products/${slug}/${id}/`
    } else if (id) {
      url = `/${locale}/products/${id}/`
    }
  }
  if (!url) return null
  return url.startsWith('http') ? url : (window.location.origin + (url.startsWith('/') ? url : '/' + url))
}

function navigateToProduct(item) {
  if (item.has_pdp) {
    const url = getProductUrl(item)
    if (url) window.location.href = url
  }
}

defineExpose({ openClearConfirm: () => { showClearConfirm.value = true } })
</script>
